import React, { useState } from "react";
import api, { formatApiError } from "@/utils/api";

/**
 * School logo upload — called during signup before submitting the full payload.
 * On successful upload, parent receives logo_path (storage key) + display_url.
 */
export default function LogoUpload({ onUploaded, testidPrefix = "logo" }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploaded, setUploaded] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Must be JPG, PNG, or WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Must be under 2 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/auth/upload-logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(data);
      onUploaded(data.logo_path, data.display_url);
    } catch (e) {
      setError(formatApiError(e));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-sm text-slate-300">
        School logo <span className="text-rose-400">*</span>
      </label>
      <p className="text-xs text-slate-500 mt-0.5 mb-2">
        Used on student reports so parents see your branding. JPG/PNG/WebP, under 2 MB.
      </p>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-lg border border-slate-700 bg-[#0E1A3A] flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="Logo preview" className="w-full h-full object-contain" data-testid={`${testidPrefix}-preview`} />
          ) : (
            <span className="text-xs text-slate-500">No logo</span>
          )}
        </div>
        <label className="cursor-pointer px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm transition">
          {uploading ? "Uploading…" : uploaded ? "Replace" : "Choose file"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid={`${testidPrefix}-input`}
          />
        </label>
      </div>
      {error && <p className="text-xs text-rose-300 mt-2" data-testid={`${testidPrefix}-error`}>{error}</p>}
      {uploaded && !error && <p className="text-xs text-emerald-300 mt-2">Logo uploaded successfully.</p>}
    </div>
  );
}
